import { useCallback, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useDefaultSetups } from 'app/frontend/base/config/queries'
import {
  updateDefaultSetupSelections,
  upsertDefaultSetup,
} from 'app/frontend/base/api/default_setups'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import { matchesDefaultSetup } from './defaultSetupEntries'
import useDefaultSetupOptimisticUpdate, {
  applyDefaultSetupSelection,
} from './useDefaultSetupOptimisticUpdate'

const tempSetupId = (encounterId: number) => -encounterId

const useDefaultSetupMutations = (teamId: number, difficulty: Difficulty) => {
  const queryKey = ['defaultSetups', teamId] as const
  const { data: setups, isPending: isSetupsPending } = useDefaultSetups(teamId)
  const optimisticUpdate = useDefaultSetupOptimisticUpdate(queryKey)
  const createSetupPromisesRef = useRef<
    Map<number, Promise<wowaudit.DefaultSetup>>
  >(new Map())

  const setupFor = useCallback(
    (encounterId: number) =>
      setups?.find((entry) =>
        matchesDefaultSetup(entry, encounterId, difficulty),
      ),
    [difficulty, setups],
  )

  const persistSetup = useCallback(
    (encounterId: number, nextEnabled: boolean) => {
      const setup = setupFor(encounterId)
      if (setup && setup.id > 0) {
        return upsertDefaultSetup(teamId, {
          encounter_id: encounterId,
          difficulty,
          enabled: nextEnabled,
        })
      }
      let createPromise = createSetupPromisesRef.current.get(encounterId)
      if (!createPromise) {
        createPromise = upsertDefaultSetup(teamId, {
          encounter_id: encounterId,
          difficulty,
          enabled: nextEnabled,
        }).catch((error) => {
          createSetupPromisesRef.current.delete(encounterId)
          throw error
        })
        createSetupPromisesRef.current.set(encounterId, createPromise)
      }
      return createPromise.then(async (created) => {
        if (created.enabled === nextEnabled) return created
        return upsertDefaultSetup(teamId, {
          encounter_id: encounterId,
          difficulty,
          enabled: nextEnabled,
        })
      })
    },
    [difficulty, setupFor, teamId],
  )

  const upsertMutation = useMutation({
    mutationFn: ({
      encounterId,
      enabled,
    }: {
      encounterId: number
      enabled: boolean
    }) => persistSetup(encounterId, enabled),
    onMutate: async ({ encounterId, enabled: nextEnabled }) => {
      const previous = await optimisticUpdate.snapshot()
      return optimisticUpdate.registerOptimisticPatch(previous, (current) => {
        const existing = current.find((entry) =>
          matchesDefaultSetup(entry, encounterId, difficulty),
        )
        if (existing) {
          return current.map((entry) =>
            entry.id === existing.id
              ? { ...entry, enabled: nextEnabled }
              : entry,
          )
        }
        return [
          ...current,
          {
            id: tempSetupId(encounterId),
            encounterId,
            difficulty,
            enabled: nextEnabled,
            selections: {},
          },
        ]
      })
    },
    onError: (_error, _variables, context) => {
      optimisticUpdate.rollback(context)
    },
    onSuccess: (data, variables, context) => {
      createSetupPromisesRef.current.set(
        variables.encounterId,
        Promise.resolve(data),
      )
      optimisticUpdate.applyServerUpdate(data, context)
    },
  })

  const selectionMutation = useMutation({
    mutationFn: ({
      encounterIds,
      selection,
    }: {
      encounterIds: number[]
      selection: {
        character_id: number
        selected?: boolean
        role?: string
        spec_id?: number | null
        alt_character_id?: number
      }
    }) =>
      updateDefaultSetupSelections(teamId, {
        difficulty,
        encounter_ids: encounterIds,
        selection,
      }),
    onMutate: async ({ encounterIds, selection }) => {
      const previous = await optimisticUpdate.snapshot()
      return optimisticUpdate.registerOptimisticPatch(previous, (current) =>
        encounterIds.reduce((next, encounterId) => {
          const existing = next.find((entry) =>
            matchesDefaultSetup(entry, encounterId, difficulty),
          )
          const withSetup = existing
            ? next
            : [
                ...next,
                {
                  id: tempSetupId(encounterId),
                  encounterId,
                  difficulty,
                  enabled: false,
                  selections: {},
                },
              ]
          return applyDefaultSetupSelection(
            withSetup,
            existing?.id ?? tempSetupId(encounterId),
            selection,
          )
        }, current ?? []),
      )
    },
    onError: (_error, _variables, context) => {
      optimisticUpdate.rollback(context)
    },
    onSuccess: (data, _variables, context) => {
      data.forEach((setup) => {
        createSetupPromisesRef.current.set(
          setup.encounterId,
          Promise.resolve(setup),
        )
      })
      optimisticUpdate.applyServerUpdates(data, context)
    },
  })

  const upsertEnabled = useCallback(
    (encounterId: number, enabled: boolean) => {
      upsertMutation.mutate({ encounterId, enabled })
    },
    [upsertMutation],
  )

  const isUpsertPending = useCallback(
    (encounterId: number) =>
      upsertMutation.isPending &&
      upsertMutation.variables?.encounterId === encounterId,
    [upsertMutation.isPending, upsertMutation.variables],
  )

  const updateSelection = useCallback(
    (
      encounterId: number | readonly number[],
      characterId: number,
      updates: {
        role?: string
        specId?: number | null
        characterId?: number
        selected?: boolean
      },
    ) => {
      const encounterIds = (
        Array.isArray(encounterId) ? encounterId : [encounterId]
      ).filter((id) => Number.isFinite(id))
      if (encounterIds.length === 0) return

      selectionMutation.mutate({
        encounterIds,
        selection: {
          character_id: characterId,
          selected: updates.selected,
          role: updates.role,
          spec_id: updates.specId,
          alt_character_id: updates.characterId,
        },
      })
    },
    [selectionMutation],
  )

  return {
    setups,
    isSetupsPending,
    isSaving: upsertMutation.isPending || selectionMutation.isPending,
    setupFor,
    upsertEnabled,
    isUpsertPending,
    updateSelection,
  }
}

export default useDefaultSetupMutations
